"use client";

import React from "react";
import { motion } from "framer-motion";
import { Quote, BadgeCheck } from "lucide-react";
import { Footer } from "@/components/sections/premium/ManifestoSection";
import { testimonials, communityVoices, type Testimonial } from "@/components/sections/premium/testimonialsData";

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

const Card = ({ t, index }: { t: Testimonial; index: number }) => (
  <motion.figure
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.5, delay: (index % 3) * 0.05 }}
    className="mb-5 md:mb-6 break-inside-avoid rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-colors duration-500 p-7 md:p-8"
  >
    <div className="flex items-center justify-between mb-5">
      <Quote className="w-7 h-7 text-primary/70" />
      {t.category && <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/30">{t.category}</span>}
    </div>

    <blockquote className="font-sans text-lg md:text-xl leading-relaxed text-white/80">
      &ldquo;{t.quote}&rdquo;
    </blockquote>

    {t.metric && (
      <div className="mt-5">
        <span className="inline-block font-mono text-[9px] uppercase tracking-[0.2em] border border-primary/30 text-primary px-3 py-1.5 rounded-full">
          {t.metric}
        </span>
      </div>
    )}

    <figcaption className="mt-7 pt-6 border-t border-white/10 flex items-center gap-4">
      <div className="w-11 h-11 shrink-0 rounded-full border border-white/15 bg-primary/10 flex items-center justify-center font-display text-sm text-primary">
        {initials(t.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-sans text-sm text-white font-medium leading-tight truncate">{t.name}</p>
          <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-white/40 mt-1 truncate">
          {[t.role, t.company].filter(Boolean).join(" · ")}
          {t.inProgress && <span className="text-primary/80"> · In progress</span>}
        </p>
      </div>
    </figcaption>
  </motion.figure>
);

export const TestimonialsContent = () => {
  return (
    <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[70vh] w-full flex flex-col justify-end p-6 md:p-12 overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,_rgba(255,255,255,0.06),_transparent_55%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a]" />
        </div>

        <div className="relative z-10 flex justify-between items-start mt-24 mb-auto">
          <motion.span
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
          >
            DOTSLASHLAB / IN THEIR WORDS
          </motion.span>
          <motion.span
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest"
          >
            FOUNDERS · CTOs <br /> GOVERNMENTS
          </motion.span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10"
        >
          <h1 className="font-display text-[15vw] md:text-[12vw] leading-[0.8] tracking-tight uppercase">
            CLIENT <span className="text-transparent italic" style={{ WebkitTextStroke: "2px #f4f4f0" }}>VOICES.</span>
          </h1>
          <p className="mt-8 font-sans text-lg md:text-2xl text-white/60 max-w-3xl leading-relaxed">
            Founders, CTOs and government teams on what we built together, and what happened next.
          </p>
        </motion.div>
      </section>

      {/* GRID */}
      <section className="relative z-10 bg-[#0a0a0a] px-4 md:px-12 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto mb-12 md:mb-16 flex flex-wrap items-center gap-x-8 gap-y-4 border-b border-white/10 pb-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4 text-primary" /> {testimonials.length + communityVoices.length} verified voices
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            Real clients · Real projects
          </span>
        </div>

        <div className="max-w-[1400px] mx-auto columns-1 md:columns-2 lg:columns-3 gap-5 md:gap-6">
          {testimonials.map((t, i) => (
            <Card key={t.id} t={t} index={i} />
          ))}
        </div>

        {/* COMMUNITY / THIRD-PARTY VOICES */}
        {communityVoices.length > 0 && (
          <div className="max-w-[1400px] mx-auto mt-24 md:mt-32">
            <div className="flex items-center gap-4 mb-10">
              <span className="font-mono text-xs text-primary uppercase tracking-[0.3em]">/ From the wider community</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-5 md:gap-6">
              {communityVoices.map((t, i) => (
                <Card key={t.id} t={t} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="max-w-[1400px] mx-auto mt-20 md:mt-28 text-center">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.9]">
            Want to be the <span className="text-primary italic">next one?</span>
          </h2>
          <a
            href="/contact"
            className="mt-10 inline-flex items-center gap-3 bg-[#f4f4f0] text-[#0a0a0a] hover:bg-primary hover:text-white px-8 py-4 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] transition-colors duration-500"
          >
            Start a project
          </a>
        </div>
      </section>

      <Footer />

      <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
    </main>
  );
};
